import {Message} from '../../commons/src/types'
import {
  CountryCode,
  MaterialConfigId,
  MaterialId,
  PrintingMethodId,
  ProviderId,
  ShippingId,
  ShippingType,
} from './types'

import {OrderTagType} from './components/order-tag/OrderTag'

export type EmptyObject = Record<string, never>

export type OrderId = string
export type OrderNumber = string
export type ModelId = string
export type FileUnit = 'mm' | 'cm' | 'in'
export type ModelSceneId = string
export type QuoteId = string
export type AdditionalCostId = string
export type InvoiceId = string
export type RefundId = string
export type AttachmentId = string
export type IsoDate = string

export type Location = {
  city: string
  zipCode: string
  stateCode: string
  countryCode: CountryCode
}

type Address = Location & {
  firstName: string
  lastName: string
  address: string
  addressLine2?: string
  companyName?: string
}

export type BillingAddress = Address & {
  isCompany: boolean
  vatId?: string
}

export type ShippingAddress = Address & {
  phoneNumber: string
}

export type Voucher = {
  code: string
  error?: string
}

export type ModelDimensions = {
  x: number
  y: number
  z: number
}

export type BackendModelAttachment = {
  id: string
  created: string
  name: string
}

export type BackendModel = {
  modelId: ModelId
  fileName: string
  fileUnit: FileUnit
  area: number
  volume: number
  dimensions: ModelDimensions
  thumbnailUrl: string
  sceneId?: ModelSceneId | null
  attachments?: BackendModelAttachment[]
  cncAnalysis?: {
    axisRequirement: 3 | 4 | 5 | null
    setupCount: number
  }
}

export type Amount = {
  currency: string
  netPrice: number
  minimumProductionPrice?: number
  productionFee?: number // min prod cost difference
  undiscountedProductionFee?: number
  originalPrice?: number // not included for old orders
  vendorProductionPrice?: number // included for old orders
}

export type Amounts = {
  cartItems: Amount | EmptyObject
  shipping: Amount | EmptyObject
  invoiceable?: {
    production?: number
    shipping?: number
    additionalCosts?: number
  }
  additionalCosts?: Amount // only includes data for paid additional costs
}

export type QuoteType =
  | 'expedited'
  | 'economy'
  | 'infill_40'
  | 'infill_60'
  | 'infill_80'
  | 'infill_95'
  | 'infill_100'
  | 'tolerance_004'
  | 'tolerance_003'
  | 'tolerance_002'
  | 'tolerance_001'
  | 'tolerance_tighter_001'

export type Quote = {
  quoteId: QuoteId
  vendorId: ProviderId
  modelId: ModelId
  materialConfigId: MaterialConfigId
  printingMethodId?: PrintingMethodId // old orders may not have a printingMethodId
  price: number
  vendorProductionPrice?: number // included in old orders
  amounts: Amount
  quantity: number
  currency: string
  productionTimeFast: number
  productionTimeSlow: number
  scale: number
  types?: QuoteType[]
  materialName: string
  vendorMaterialName?: string // contains the materialName vendors can set if available
  note?: string
  created: IsoDate
}

export enum OrderStatusType {
  ORDERED = 'ordered',
  IN_PRODUCTION = 'in_production',
  SHIPPED = 'shipped',
  RECEIVED = 'received',
  BLOCKED = 'blocked',
  CANCELLED = 'cancelled',
  UNKNOWN = 'unknown',
}

export type HistoryItem = {
  date: string
  status: OrderStatusType
  // Note: additionalData can hold more and various information which we don't use
  additionalData?: {
    trackingUrl?: string
    trackingNumber?: string
  }
}

export type OrderShipping = {
  shippingId: ShippingId
  type: ShippingType
  vendorId: string
  country: CountryCode
  price: number | Array<{weight: number; price: number}>
  amounts: Amount
  name: string
  deliveryTime?: string // e.g. "1-5" // not available for credit notes
  carrier?: string
}

export type Customer = ShippingAddress & {
  currency: string
  vatId?: string
}

export type AdditionalCost = {
  id: AdditionalCostId
  created: string
  vendorId: ProviderId
  description: string
  currency: string
  net: number
  amounts: Amount
  status: {
    paid: boolean
  }
}

export type CustomsInformation = {
  industry: string
  description: string
}

export type Order = {
  orderNumber: OrderNumber
  orderId: OrderId
  created: string
  hasReview: boolean
  isManual?: boolean
  shippingDueDate: string
  note: string | null
  printingService: {
    status: OrderStatusType
    trackingUrl?: string
    trackingNumber?: string
  }
  history: HistoryItem[]
  quotes: Quote[]
  models: BackendModel[]
  amounts: Amounts
  messages?: Message[]
  additionalCosts?: AdditionalCost[]
  refundRequests?: OrderRefund[]
  customer: Customer
  shipping: OrderShipping | EmptyObject
  customsInformation: CustomsInformation | null
  appId?: string
}

export type OrderRowContent = {
  orderNumber: OrderNumber
  orderId: OrderId
  customerName: string
  status: OrderStatusType
  modelsCount: number
  quantity: number
  materialIds: MaterialId[]
  printingMethodIds: PrintingMethodId[]
  tags: OrderTagType[]
  productionPrice: number
  formattedProductionPrice: string
  shippingPrice: number
  formattedShippingPrice: string
  additionalCost: number
  formattedAdditionalCost: string
  messagesCount: number
  unreadMessagesCount: number
  hasUnreadMessages: boolean
  shippingDueDate: IsoDate
  placementDate: IsoDate
  hasReview: boolean
}

export type QuoteRowContent = {
  quoteId: string
  modelThumbnailUrl: string
  fileName: string
  quantity: number
  materialName: string
  colorHexCode: string | undefined
  colorImage: string | undefined
  printingMethodName: string
  tags: OrderTagType[]
  productionPrice: string
}

export type QuoteDetailRowContent = {
  quoteId: string
  modelThumbnailUrl: string
  modelId: ModelId
  fileName: string
  quantity: number
  scale: number
  dimensions: string
  material: string
  colorHexCode: string | undefined
  colorImage: string | undefined
  printingMethod: string
  infill?: string
  tolerance?: string
  leadTime: string
  price: string
  originalPrice: string
  productionOption: 'expedited' | 'economy' | null
  types?: QuoteType[] | undefined
  note?: string
  attachments?: BackendModelAttachment[]
  cncAnalysis?: {
    axisRequirement: 3 | 4 | 5 | null
    setupCount: number
  }
}

export enum OrderDetailTab {
  DETAILS = 'details',
  MESSAGES = 'messages',
  REVIEW = 'review',
}

export type InvoiceAmounts = {
  subTotal: number
  additionalCostsTotal?: number
  net: number
  vendorProductionPrice: number
  productionPremium?: number
  shippingTotal: number
  total: number
  currency: string
}

export type InvoiceAmountsV2 = {
  subTotal: number
  additionalCostsTotal: number
  net: number
  shippingTotal: number
  total: number
  currency: string
}

export type ProviderInvoiceables = {
  margin: number
  productionMinusMargin: number
  additionalCostsMinusMargin: number
  productionPremiumShare: number
  shipping: number
  totalToInvoice: number
  currency: string
}

export type Invoiceable = {
  additionalCosts: number
  production: number
  shipping: number
  total: number
}

export type OrderInvoice = {
  invoiceId: InvoiceId
  orderId: OrderId
  orderNumber: OrderNumber
  vendorId: ProviderId
  orderDate: IsoDate
  created: IsoDate
  amounts: InvoiceAmounts
  providerInvoiceables: ProviderInvoiceables
}

export type OrderInvoiceV2 = Omit<OrderInvoice, 'providerInvoiceables' | 'amounts'> & {
  invoiceable: Invoiceable
  amounts: InvoiceAmountsV2
}

export type OrderCreditNote = {
  orderId: OrderId
  orderNumber: OrderNumber
  orderDate?: IsoDate
  created: IsoDate
  amounts: InvoiceAmounts
  providerInvoiceables: ProviderInvoiceables
}

export type OrderCreditNoteV2 = Omit<OrderCreditNote, 'providerInvoiceables' | 'amounts'> & {
  invoiceable: Invoiceable
  amounts: InvoiceAmountsV2
}

export type InvoiceRowContent = {
  orderNumber: OrderNumber
  orderId: OrderId
  placementDate?: IsoDate
  invoiceDate?: IsoDate
  currency: string
  production: number
  additionalCosts: number
  margin: number
  productionMinusMargin: number
  additionalCostsMinusMargin: number
  productionPremiumShare: number
  shipping: number
  totalToInvoice: number
  hasAdditionalCosts: boolean
}

export type InvoiceV2RowContent = {
  orderNumber: OrderNumber
  orderId: OrderId
  placementDate?: IsoDate
  creationDate?: IsoDate
  currency: string
  totalCustomerPrice: number
  invoiceableProductionPrice: number
  invoiceableShippingPrice: number
  invoiceableAdditionalCostsPrice: number
  invoiceableTotalPrice: number
  hasAdditionalCosts: boolean
}

export type CreditNoteRowContent = Omit<InvoiceRowContent, 'invoiceDate'> & {
  creationDate?: IsoDate
}

export type CreditNoteV2RowContent = Omit<InvoiceV2RowContent, 'invoiceDate'> & {
  creationDate?: IsoDate
}

export enum RefundType {
  FULL = 'full',
  PARTIAL = 'partial',
  CUSTOM = 'custom',
}

export enum RefundStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export type RefundQuote = {
  quoteId: string
  types: string[]
}

export enum RefundCustomAmountType {
  PRODUCTION = 'production',
  SHIPPING = 'shipping',
  ADDITIONAL_COST = 'additional-cost',
}

export type RefundCustomAmount = {
  of: RefundCustomAmountType
  net: number
  description: string
  additionalCostId?: AdditionalCostId
}

export type OrderRefund = {
  id: string
  status: RefundStatus
  created: string
  refundType: RefundType
  reason: string
  quotes?: RefundQuote[]
  shippings?: ShippingId[]
  additionalCosts?: AdditionalCostId[]
  customAmount?: RefundCustomAmount
  totalNetAmount: number
}

export enum PricingVersion {
  V1 = 1,
  V2 = 2,
}
